import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { StripeCardElement, loadStripe } from '@stripe/stripe-js';
import { useTranslation } from 'next-i18next';

import { asyncPost } from '@lib/fetch';

import { TAPPAY_ID, TAPPAY_KEY } from '../constants';

export enum PaymentEnum {
  Tappay = 'tappay',
  Stripe = 'stripe',
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

const PaymentContext = createContext<{
  paymentElement: React.ReactNode;
  onSubmit: any;
  canGetPrime?: boolean;
  type: PaymentEnum;
} | null>(null);

type Props = {
  children: ReactNode;
};

const styleToString = (style: any) => {
  return Object.keys(style).reduce(
    (acc, key) =>
      acc +
      key
        .split(/(?=[A-Z])/)
        .join('-')
        .toLowerCase() +
      ':' +
      style[key] +
      ';',
    ''
  );
};

export function StripeProvider({ children }: Props) {
  const stripe = useStripe();
  const elements = useElements();
  const [canGetPrime, setCanGetPrime] = useState(false);

  const handleSubmit = useCallback(
    async ({
      email,
      name,
      price,
      commission,
      eventId,
    }: {
      email: string;
      name: string;
      price: number;
      commission: number;
      eventId: string;
    }) => {
      if (!stripe || !elements) {
        // Stripe.js has not yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      const paymentIntent = (await asyncPost({
        url: `/api/stripe/paymentIntent`,
        body: {
          price,
          commission,
          eventId,
        },
      })) as { clientSecret: string };

      if (!paymentIntent?.clientSecret) {
        throw new Error('Generate payment intent failed');
      }

      const result = await stripe.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement) as StripeCardElement,
            billing_details: {
              email,
              name,
            },
          },
        }
      );

      if (result.error) {
        // Show error to your customer (e.g., insufficient funds)
        throw new Error(result.error.message);
      } else {
        // The payment has been processed!
        if (result.paymentIntent.status === 'succeeded') {
          // Show a success message to your customer
          // There's a risk of the customer closing the window before callback
          // execution. Set up a webhook or plugin to listen for the
          // payment_intent.succeeded event that handles any business critical
          // post-payment actions.
          return result.paymentIntent.id;
        }

        // FIXME: handle other scenario for different status other than succeeded
      }
    },
    [elements, stripe]
  );

  const contextValue = useMemo(
    () => ({
      paymentElement: (
        <CardElement
          onChange={(event) => {
            if (event.complete) {
              setCanGetPrime(true);
            } else if (event.error) {
              setCanGetPrime(false);
            }
          }}
          options={CARD_ELEMENT_OPTIONS}
        />
      ),
      onSubmit: handleSubmit,
      type: PaymentEnum.Stripe,
      canGetPrime,
    }),
    [canGetPrime, handleSubmit]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
}

export function TappayProvider({ children }: Props) {
  const { t } = useTranslation();
  const [canGetPrime, setCanGetPrime] = useState(false);

  const handleSDKSetup = () => {
    if (!window.TPDirect) return;
    window.TPDirect.setupSDK(
      TAPPAY_ID,
      TAPPAY_KEY,
      process.env.NODE_ENV === 'production' ? 'production' : 'sandbox'
    );
  };
  const handleCardSetup = () => {
    window.TPDirect.card.setup({
      fields: {
        number: {
          // css selector
          element: '#card-number',
          placeholder: '**** **** **** ****',
        },
        expirationDate: {
          // DOM object
          element: document.getElementById('card-expiration-date'),
          placeholder: 'MM / YY',
        },
        ccv: {
          element: '#card-ccv',
          placeholder: 'ccv',
        },
      },
      styles: {
        // Style all elements
        input: {
          color: 'gray',
        },
        // Styling ccv field
        'input.ccv': {
          'font-size': '1em',
        },
        // Styling expiration-date field
        'input.expiration-date': {
          'font-size': '1em',
        },
        // Styling card-number field
        'input.card-number': {
          'font-size': '1em',
        },
        // style focus state
        ':focus': {
          // 'color': 'black'
        },
        // style valid state
        '.valid': {
          color: 'green',
        },
        // style invalid state
        '.invalid': {
          color: 'red',
        },
        // Media queries
        // Note that these apply to the iframe, not the root window.
        '@media screen and (max-width: 400px)': {
          input: {
            color: '#222',
          },
        },
      },
    });
  };

  const handleUpdateListener = () => {
    window.TPDirect.card.onUpdate(function (update: {
      canGetPrime: any;
      cardType: string;
      status: { number: number; expiry: number; ccv: number };
    }) {
      // update.canGetPrime === true
      // --> you can call TPDirect.card.getPrime()
      if (update.canGetPrime) {
        setCanGetPrime(true);
      } else {
        setCanGetPrime(false);
      }

      // cardTypes = ['mastercard', 'visa', 'jcb', 'amex', 'unionpay','unknown']
      if (update.cardType === 'visa') {
        // Handle card type visa.
      }

      // number 欄位是錯誤的
      if (update.status.number === 2) {
        // setNumberFormGroupToError()
      } else if (update.status.number === 0) {
        // setNumberFormGroupToSuccess()
      } else {
        // setNumberFormGroupToNormal()
      }

      if (update.status.expiry === 2) {
        // setNumberFormGroupToError()
      } else if (update.status.expiry === 0) {
        // setNumberFormGroupToSuccess()
      } else {
        // setNumberFormGroupToNormal()
      }

      if (update.status.ccv === 2) {
        // setNumberFormGroupToError()
      } else if (update.status.ccv === 0) {
        // setNumberFormGroupToSuccess()
      } else {
        // setNumberFormGroupToNormal()
      }
    });
  };

  const onSubmit = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (window.TPDirect) {
        const { TPDirect } = window;
        // 取得 TapPay Fields 的 status
        const tappayStatus = TPDirect.card.getTappayFieldsStatus();

        // 確認是否可以 getPrime
        if (tappayStatus.canGetPrime === false) {
          reject(t('Tappay error'));
          return;
        }

        // Get prime
        TPDirect.card.getPrime(
          (result: { status: number; msg: any; card: { prime: string } }) => {
            if (result.status !== 0) {
              reject(`${t('Tappay error')}: ${result.msg}`);
              return;
            }

            resolve(result.card.prime);
            // send prime to your server, to pay with Pay by Prime API .
            // Pay By Prime Docs: https://docs.tappaysdk.com/tutorial/zh/back.html#pay-by-prime-api
          }
        );
        return;
      }
      return console.warn('TPDirect sdk not initialized');
    });
  }, [t]);

  useEffect(() => {
    setTimeout(() => {
      handleSDKSetup();
      handleCardSetup();
      handleUpdateListener();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue = useMemo(
    () => ({
      paymentElement: (
        <>
          <style
            dangerouslySetInnerHTML={{
              __html: `.tpfield {
        height: 40px;
        width: 300px;
        border: 1px solid gray;
        border-radius: 0.25em;
        margin: 0.25rem 0 0.75rem;
        padding: 0.5em 0.75em;
        font-size: 0.75rem;
        color: #2222fa;
        }
        .tpLabel {
          text-align: left;
          font-size: 0.8em;
          font-weight: bold;
        }
        `,
            }}
          ></style>
          <div>
            <div className="tpLabel">
              信用卡卡號 (支援 Visa, Master Card, JCB, AMEX)
            </div>
            <div className="tpfield" id="card-number"></div>
            <div className="tpLabel">卡片到期日</div>
            <div className="tpfield" id="card-expiration-date"></div>
            <div className="tpLabel">卡片後三碼</div>
            <div className="tpfield" id="card-ccv"></div>
          </div>
        </>
      ),
      onSubmit,
      canGetPrime,
      type: PaymentEnum.Tappay,
    }),
    [canGetPrime, onSubmit]
  );
  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
}

export const usePayment = () => {
  const paymentContextValues = useContext(PaymentContext);

  if (!paymentContextValues) {
    throw new Error('paymentContext must be initialized first');
  }

  return paymentContextValues;
};
